/**
 * How wound tables are written.
 *
 * A table is 4 damage types × 9 zones × 6 levels. Writing 200-odd blocks of
 * six numbers by hand would guarantee two things: typos, and drift — three
 * balance passes later, `cut/arm/3` and `cut/leg/3` would bleed at unrelated
 * rates for no reason anybody could remember.
 *
 * So the numbers are factored the way they actually vary:
 *
 * - a **type profile** says what a cut is like at each level, in the abstract;
 * - a **zone shape** says how that zone changes it — a neck bleeds several
 *   times harder than a hand, a skull breaks where a thigh does not;
 * - **narration is written per cell**, by hand, always. That is the part that
 *   should not be generated, and the part `MIN_NARRATION_TEMPLATES` guards.
 *
 * The result stays tunable at the level a designer thinks in ("punctures bleed
 * more, blunt breaks more, the neck is a death sentence") rather than at the
 * level of 1,152 individual numbers.
 */
/** Keeps authored numbers readable and free of float dust in golden tests. */
function round(value) {
    return Math.round(value * 100) / 100;
}
function entry(row, shape, level, narration) {
    const extra = shape.fx?.[level] ?? [];
    const effects = [...new Set([...(row.fx ?? []), ...extra])];
    return {
        bleedRate: round(row.bleed * (shape.bleed ?? 1)),
        shock: round(row.shock * (shape.shock ?? 1)),
        pain: round(row.pain * (shape.pain ?? 1)),
        impair: Math.min(1, round(row.impair * (shape.impair ?? 1))),
        knockdownMod: round(row.knock + (shape.knock ?? 0)),
        infectionMod: round(row.infect * (shape.infect ?? 1)),
        effects,
        narration,
    };
}
/** Build one `(type, zone)` ladder from a profile, a zone shape and prose. */
export function ladder(profile, shape, narration) {
    return [
        entry(profile[0], shape, 0, narration[0]),
        entry(profile[1], shape, 1, narration[1]),
        entry(profile[2], shape, 2, narration[2]),
        entry(profile[3], shape, 3, narration[3]),
        entry(profile[4], shape, 4, narration[4]),
        entry(profile[5], shape, 5, narration[5]),
    ];
}
/** No extra effects at levels 0–3; the given ones at 4 and 5. */
export function fxAt4and5(atFour, atFive) {
    return [[], [], [], [], atFour, atFive];
}
//# sourceMappingURL=authoring.js.map